import { Alert, Group, Stack } from "@mantine/core"
import { FileWithPath, MIME_TYPES } from "@mantine/dropzone"
import { notifications } from "@mantine/notifications"
import { IconChecks } from "@tabler/icons-react"
import {
  Button,
  Card,
  DEFAULT_MAX_SIZE,
  DocumentUploader,
  FileListItem,
  Loader,
} from "@tfg/ui"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { IngestionSettings } from "~/pages/Admin/Ingestion/ProductImport/components/IngestionSettings"
import { useFindIngestionSettingsQuery } from "~/pages/Admin/Ingestion/ProductImport/hooks/useFindIngestionSettings.ts"
import { useVerifyBlankProductImportMutation } from "~/pages/Admin/Ingestion/ProductImport/hooks/useVerifyBlankProductImportMutation"
import { useVerifyProductImportMutation } from "~/pages/Admin/Ingestion/ProductImport/hooks/useVerifyProductImportMutation"

export function IngestionDocumentValidate() {
  const { t } = useTranslation()
  const [owner, setOwner] = useState<string>("")
  const [selectedFile, setSelectedFile] = useState<File>()

  const { mutate: verifyProductImport, isLoading: isVerifyingProductImport } =
    useVerifyProductImportMutation({
      onSuccess: () => {
        notifications.show({
          title: "File uploaded",
          message: "File is being validated",
          color: "green",
        })
        setSelectedFile(undefined)
      },
      onError: () => {
        notifications.show({
          title: "Something went wrong",
          message: "File non validated",
          color: "red",
        })
      },
    })

  const {
    mutate: verifyBlankProductImport,
    isLoading: isVerifyingBlankProductImport,
  } = useVerifyBlankProductImportMutation({
    onSuccess: () => {
      notifications.show({
        title: "Blank file validated",
        message: "The blank file has been validated successfully",
        color: "green",
      })
    },
    onError: () => {
      notifications.show({
        title: "Something went wrong",
        message: "File non validated",
        color: "red",
      })
    },
  })

  const { data: ingestionSettings, isLoading } = useFindIngestionSettingsQuery()
  const [isBlankIngestion, setIsBlankIngestion] = useState<boolean>(false)

  const handleDrop = (files: FileWithPath[]) => {
    setSelectedFile(files[0])
  }

  if (!ingestionSettings) {
    return <Alert>No ingestion settings found</Alert>
  }

  const handleSubmit = () => {
    if (!selectedFile) {
      return
    }
    if (isBlankIngestion) {
      verifyBlankProductImport({ file: selectedFile, clientUserEmail: owner })
      return
    }
    verifyProductImport({ file: selectedFile, clientUserEmail: owner })
  }

  if (isVerifyingProductImport || isVerifyingBlankProductImport) {
    return <Loader />
  }

  return (
    <Card style={{ justifyContent: "space-between" }}>
      <Group align="stretch" style={{ width: "100%" }} wrap="nowrap">
        <Stack
          px="sm"
          h="auto"
          w="100%"
          style={{ flexBasis: "40%" }}
          justify="space-between"
        >
          <Group h="70%" w="100%">
            {selectedFile ? (
              <FileListItem
                fileName={selectedFile.name}
                onRemove={() => setSelectedFile(undefined)}
                w="100%"
                h="100%"
                size="lg"
              />
            ) : (
              <DocumentUploader
                onDrop={handleDrop}
                maxFiles={1}
                width="100%"
                maxSize={10 * 1024 ** 2}
                accept={[MIME_TYPES.csv]}
                title={t("documentUpload.title")}
                helpText={t("documentUpload.helptext", {
                  maxSize: `${DEFAULT_MAX_SIZE / 1024 / 1024}`,
                })}
              />
            )}
          </Group>
          <Group>
            <Button
              display="inline-block"
              fullWidth={true}
              variant="primary"
              onClick={handleSubmit}
              disabled={!(selectedFile && owner)}
              icon={<IconChecks />}
            >
              Validate File
            </Button>
          </Group>
        </Stack>
        {isLoading ? (
          <Loader />
        ) : ingestionSettings ? (
          <IngestionSettings
            mode="verify"
            selectedFile={selectedFile}
            ingestionSettings={ingestionSettings.data}
            isBlankIngestion={isBlankIngestion}
            setIsBlankIngestion={setIsBlankIngestion}
            owner={owner}
            setOwner={setOwner}
          />
        ) : null}
      </Group>
    </Card>
  )
}
